<template>
  <div class="app-progress-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <!-- Top Navigation -->
    <div class="top-nav">
      <div class="nav-container">
        <div class="nav-brand">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M28 10.6667L16 22.6667L4 10.6667M16 4L4 10.6667V21.3333C4 23.9 9.33333 28 16 28C22.6667 28 28 23.9 28 21.3333V10.6667L16 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>MANAGE APP PROGRESS</span>
        </div>
        <button class="nav-btn" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back to Settings
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="progress-container">
      <!-- Hero Section -->
      <div class="progress-hero">
        <div class="hero-text">
          <h1 class="hero-title">Update Customer Journeys</h1>
          <p class="hero-subtitle">Pick a customer, review their journey, and set the next stage</p>
        </div>
        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-label">Customers</div>
            <div class="stat-value">{{ customers.length }}</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-label">Completed</div>
            <div class="stat-value">{{ completedCount }}</div>
          </div>
        </div>
      </div>

      <!-- Main Card -->
      <div class="main-card">
        <h2 class="card-title">Select Customer and Update Progress</h2>

        <!-- Customer Search/Select -->
        <div class="customer-select-section">
          <div class="search-wrapper">
            <svg class="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or email..."
              @focus="showDropdown = true"
              @input="showDropdown = true"
            />
            <button v-if="searchQuery" class="clear-btn" @click="clearSearch">✕</button>
          </div>

          <!-- Dropdown Results -->
          <div v-if="showDropdown && filteredCustomers.length > 0" class="customer-dropdown">
            <div
              v-for="customer in filteredCustomers"
              :key="customer.uid"
              class="customer-option"
              @click="selectCustomer(customer)"
            >
              <div class="customer-avatar">{{ customer.initials }}</div>
              <div class="customer-info">
                <div class="customer-name">{{ customer.name || '(No name)' }}</div>
                <div class="customer-email">{{ customer.email }}</div>
              </div>
              <div :class="['progress-badge', `badge-${getStageClass(customer.appProgress)}`]">
                {{ customer.appProgress }}
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-else-if="showDropdown && searchQuery && filteredCustomers.length === 0" class="no-results">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" stroke-width="3"/>
              <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <p>No customers found</p>
          </div>
        </div>

        <!-- Info Alert (No Customer Selected) -->
        <div v-if="!selected" class="info-alert">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span>Pick a customer above to see their journey</span>
        </div>

        <!-- Selected Customer Summary -->
        <transition name="slide-down">
          <div v-if="selected" class="customer-summary">
            <div class="summary-left">
              <div class="summary-avatar">{{ selected.initials }}</div>
              <div class="summary-info">
                <div class="summary-label">Customer</div>
                <div class="summary-name">{{ selected.name || selected.email }}</div>
              </div>
            </div>
            <div class="summary-badges">
              <div :class="['current-badge', `badge-${getStageClass(selected.appProgress)}`]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14 8L8 14L2 8M8 2V14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ selected.appProgress }}
              </div>
              <div v-if="nextStep" class="next-badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 14L14 8M14 8L8 2M14 8H2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Next: {{ nextStep }}
              </div>
            </div>
            <div class="summary-date">
              Last updated: {{ lastStageDate || '—' }}
            </div>
          </div>
        </transition>

        <!-- Timeline Journey -->
        <transition name="fade">
          <div v-if="selected" class="timeline-section">
            <h3 class="timeline-title">App Progress Journey</h3>
            <div class="timeline">
              <div
                v-for="(step, index) in progressOptions"
                :key="step"
                :class="['timeline-item', { completed: isStepComplete(step) }]"
              >
                <div class="timeline-marker">
                  <svg v-if="isStepComplete(step)" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
                <div v-if="index < progressOptions.length - 1" class="timeline-line"></div>
                <div class="timeline-content">
                  <div class="timeline-step-name">{{ step }}</div>
                  <div class="timeline-date">{{ stageDate(step) || (isStepComplete(step) ? '—' : '') }}</div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Stage Selection -->
        <transition name="fade">
          <div v-if="selected" class="stage-section">
            <div class="quick-select">
              <span class="quick-label">Quick set:</span>
              <div class="stage-chips">
                <button
                  v-for="stage in progressOptions"
                  :key="stage"
                  :class="['stage-chip', `chip-${getStageClass(stage)}`, { active: newProgress === stage }]"
                  @click="newProgress = stage"
                >
                  {{ stage }}
                </button>
              </div>
            </div>

            <div class="stage-select-wrapper">
              <label>Or choose a new stage</label>
              <select v-model="newProgress" class="stage-select">
                <option value="">Select stage...</option>
                <option v-for="stage in progressOptions" :key="stage" :value="stage">
                  {{ stage }}
                </option>
              </select>
            </div>
          </div>
        </transition>

        <!-- Update Button -->
        <button
          class="btn-update"
          :disabled="!selected || !newProgress || newProgress === selected?.appProgress || updating"
          @click="updateProgress"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.5 2.5 14.75 3.75 16.25 5.625M16.25 5.625V2.5M16.25 5.625H13.125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ updating ? 'Updating...' : 'Update App Progress' }}
        </button>

        <!-- Message Alert -->
        <transition name="fade">
          <div v-if="message" :class="['message-alert', messageType]">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path v-if="messageType === 'success'" d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path v-else d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            <span>{{ message }}</span>
            <button @click="message = ''">✕</button>
          </div>
        </transition>
      </div>
    </div>

    <!-- Snackbar -->
    <div v-if="snack.show" class="snackbar">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { db } from '../firebase'
import {
  collection, getDocs, updateDoc, doc, query, where, serverTimestamp, getDoc
} from 'firebase/firestore'
import router from '../router'

export default {
  name: 'ManageAppProgressView',
  setup() {
    const customers = ref([])
    const loadingCustomers = ref(true)
    const selectedUid = ref(null)
    const searchQuery = ref('')
    const showDropdown = ref(false)
    const newProgress = ref('')
    const updating = ref(false)
    const message = ref('')
    const messageType = ref('success')
    const snack = ref({ show: false, msg: '' })

    const progressOptions = ['Not Started', 'Designing', 'Developing', 'Testing', 'Completed']

    const selected = computed(() => customers.value.find(c => c.uid === selectedUid.value) || null)
    
    const filteredCustomers = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      if (!q) return customers.value
      return customers.value.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      )
    })

    const progressIndex = computed(() =>
      selected.value ? progressOptions.indexOf(selected.value.appProgress) : -1
    )

    const nextStep = computed(() => {
      if (progressIndex.value < 0) return ''
      const nxt = progressOptions[progressIndex.value + 1]
      return nxt || ''
    })

    const completedCount = computed(() =>
      customers.value.filter(c => c.appProgress === 'Completed').length
    )

    const lastStageDate = computed(() => {
      if (!selected.value) return ''
      const key = selected.value.appProgress
      return stageDate(key)
    })

    function initialsFrom(name = '', email = '') {
      const nm = (name || '').trim()
      if (nm) return nm.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase()
      return (email.split('@')[0] || '??').slice(0, 2).toUpperCase()
    }

    function getStageClass(stage) {
      switch ((stage || '').toLowerCase()) {
        case 'not started': return 'not-started'
        case 'designing': return 'designing'
        case 'developing': return 'developing'
        case 'testing': return 'testing'
        case 'completed': return 'completed'
        default: return 'default'
      }
    }

    function prettyDate(ts) {
      const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : null))
      return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    }

    function stageDate(step) {
      if (!selected.value?.appProgressDates) return ''
      return prettyDate(selected.value.appProgressDates[step])
    }

    function isStepComplete(step) {
      if (!selected.value) return false
      return progressOptions.indexOf(step) <= progressOptions.indexOf(selected.value.appProgress || 'Not Started')
    }

    async function fetchCustomers() {
      loadingCustomers.value = true
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'Customer'))
        const snap = await getDocs(q)
        customers.value = snap.docs.map(d => {
          const data = d.data() || {}
          const name = [data.firstName, data.lastName].filter(Boolean).join(' ') || (data.fullName || '')
          return {
            uid: d.id,
            email: data.email || '',
            name,
            initials: initialsFrom(name, data.email),
            appProgress: data.appProgress || 'Not Started',
            appProgressDates: data.appProgressDates || {},
            label: `${name || '(No name)'} — ${data.email || ''}`
          }
        })
      } catch (e) {
        console.error('fetchCustomers', e)
        message.value = '❌ Failed to load customers.'
        messageType.value = 'error'
      } finally {
        loadingCustomers.value = false
      }
    }

    function selectCustomer(customer) {
      selectedUid.value = customer.uid
      searchQuery.value = customer.name || customer.email
      showDropdown.value = false
      newProgress.value = ''
      message.value = ''
    }

    function clearSearch() {
      searchQuery.value = ''
      selectedUid.value = null
      showDropdown.value = false
      newProgress.value = ''
    }

    async function updateProgress() {
      if (!selected.value || !newProgress.value) return
      updating.value = true
      try {
        await updateDoc(doc(db, 'users', selected.value.uid), {
          appProgress: newProgress.value,
          [`appProgressDates.${newProgress.value}`]: serverTimestamp()
        })

        try {
          const cRef = doc(db, 'customers', selected.value.uid)
          const cSnap = await getDoc(cRef)
          if (cSnap.exists()) {
            await updateDoc(cRef, {
              appProgress: newProgress.value,
              [`appProgressDates.${newProgress.value}`]: serverTimestamp()
            })
          }
        } catch (_) { }

        snack.value = { show: true, msg: '✓ Progress updated!' }
        message.value = '✅ App progress updated successfully!'
        messageType.value = 'success'

        await fetchCustomers()
        selectedUid.value = selected.value.uid
        searchQuery.value = selected.value.name || selected.value.email
        newProgress.value = ''
      } catch (e) {
        console.error('updateProgress', e)
        message.value = '❌ Failed to update app progress.'
        messageType.value = 'error'
      } finally {
        updating.value = false
      }
    }

    function goBack() {
      router.push('/admin-settings')
    }

    function handleClickOutside(e) {
      const searchWrapper = document.querySelector('.search-wrapper')
      const dropdown = document.querySelector('.customer-dropdown')
      if (searchWrapper && !searchWrapper.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
        showDropdown.value = false
      }
    }

    onMounted(() => {
      fetchCustomers()
      document.addEventListener('click', handleClickOutside)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      customers,
      loadingCustomers,
      selectedUid,
      searchQuery,
      showDropdown,
      newProgress,
      updating,
      message,
      messageType,
      snack,
      progressOptions,
      selected,
      filteredCustomers,
      progressIndex,
      nextStep,
      completedCount,
      lastStageDate,
      getStageClass,
      stageDate,
      isStepComplete,
      selectCustomer,
      clearSearch,
      updateProgress,
      goBack
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700;800&display=swap');

.app-progress-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 47, 247, 0.3), transparent 45%),
    radial-gradient(circle at 80% 80%, rgba(255, 0, 110, 0.3), transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(0, 255, 245, 0.2), transparent 50%),
    linear-gradient(135deg, #1a0b2e 0%, #0a0118 50%, #1a0b2e 100%);
  background-size: 200% 200%;
  animation: bg-breathe 25s ease-in-out infinite;
  position: relative;
  font-family: 'Sora', sans-serif;
  padding-bottom: 80px;
}

@keyframes bg-breathe {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(0, 255, 245, 0.08) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(123, 47, 247, 0.08) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  animation: grid-flow 35s linear infinite;
  pointer-events: none;
  z-index: 0;
}

@keyframes grid-flow {
  0% { transform: translate(0, 0); }
  100% { transform: translate(60px, 60px); }
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.5;
  animation: orb-float 18s ease-in-out infinite;
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 0;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, #00fff5, transparent);
  top: -200px;
  right: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #ff006e, transparent);
  bottom: -150px;
  left: -150px;
  animation-delay: 6s;
}

@keyframes orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, -50px) scale(1.1); }
  66% { transform: translate(-40px, 40px) scale(0.9); }
}

/* Top Navigation */
.top-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: linear-gradient(90deg, rgba(123, 47, 247, 0.95), rgba(255, 0, 110, 0.95));
  backdrop-filter: blur(20px);
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0.1em;
  font-family: 'Space Mono', monospace;
}

.nav-brand svg {
  filter: drop-shadow(0 0 8px #00fff5);
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Sora', sans-serif;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.4);
}

/* Progress Container */
.progress-container {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Hero Section */
.progress-hero {
  padding: 48px 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.hero-text {
  flex: 1;
  min-width: 300px;
}

.hero-title {
  font-size: clamp(32px, 5vw, 42px);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 12px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #00fff5, #7b2ff7, #ff006e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 200%;
  animation: gradient-shift 4s ease infinite;
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-subtitle {
  font-size: 16px;
  color: #b8b8d1;
  line-height: 1.6;
}

.stats-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 28px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  backdrop-filter: blur(20px);
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Space Mono', monospace;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 32px;
  font-weight: 900;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
  line-height: 1;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
}

/* Main Card */
.main-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #ff006e;
  border-radius: 20px;
  padding: 32px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(255, 0, 110, 0.3);
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 24px;
}

/* Customer Select Section */
.customer-select-section {
  position: relative;
  margin-bottom: 24px;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: #b8b8d1;
  pointer-events: none;
}

.search-wrapper input {
  width: 100%;
  padding: 14px 14px 14px 46px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.search-wrapper input:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.search-wrapper input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.clear-btn {
  position: absolute;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* Dropdown */
.customer-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  max-height: 400px;
  overflow-y: auto;
  background: rgba(26, 11, 46, 0.98);
  border: 2px solid #00fff5;
  border-radius: 12px;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
  z-index: 10;
  animation: dropdown-appear 0.2s ease;
}

@keyframes dropdown-appear {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.customer-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.customer-option:last-child {
  border-bottom: none;
}

.customer-option:hover {
  background: rgba(0, 255, 245, 0.1);
}

.customer-avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
  flex-shrink: 0;
}

.customer-info {
  flex: 1;
  min-width: 0;
}

.customer-name {
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customer-email {
  font-size: 13px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.progress-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Space Mono', monospace;
  flex-shrink: 0;
}

.badge-not-started {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

.badge-designing {
  background: rgba(99, 102, 241, 0.2);
  color: #818cf8;
}

.badge-developing {
  background: rgba(123, 47, 247, 0.2);
  color: #a78bfa;
}

.badge-testing {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.badge-completed {
  background: rgba(74, 222, 128, 0.2);
  color: #4ade80;
}

/* No Results */
.no-results {
  padding: 40px 20px;
  text-align: center;
  color: #b8b8d1;
}

.no-results svg {
  opacity: 0.3;
  margin-bottom: 12px;
}

.no-results p {
  font-size: 15px;
}

/* Info Alert */
.info-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.15);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  color: #60a5fa;
  font-size: 14px;
  margin-bottom: 24px;
}

/* Customer Summary */
.customer-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(0, 255, 245, 0.3);
  border-radius: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  animation: summary-appear 0.3s ease;
}

@keyframes summary-appear {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.summary-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-avatar {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border-radius: 50%;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
}

.summary-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Space Mono', monospace;
}

.summary-name {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
}

.summary-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.current-badge,
.next-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Space Mono', monospace;
}

.current-badge {
  background: rgba(0, 255, 245, 0.2);
  border: 1px solid rgba(0, 255, 245, 0.4);
  color: #00fff5;
}

.next-badge {
  background: rgba(123, 47, 247, 0.2);
  border: 1px solid rgba(123, 47, 247, 0.4);
  color: #a78bfa;
}

.summary-date {
  font-size: 12px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
}

/* Timeline Section */
.timeline-section {
  margin-bottom: 32px;
  animation: fade-in 0.4s ease;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.timeline-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
}

.timeline {
  position: relative;
}

.timeline-item {
  position: relative;
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-marker {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  z-index: 1;
  transition: all 0.3s ease;
}

.timeline-item.completed .timeline-marker {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: #ffffff;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.6);
}

.timeline-item:not(.completed) .timeline-marker {
  background: rgba(156, 163, 175, 0.2);
  border: 2px solid rgba(156, 163, 175, 0.4);
  color: #9ca3af;
}

.timeline-line {
  position: absolute;
  left: 19px;
  top: 40px;
  width: 2px;
  height: calc(100% - 20px);
  background: rgba(156, 163, 175, 0.2);
}

.timeline-item.completed .timeline-line {
  background: linear-gradient(180deg, #4ade80, rgba(74, 222, 128, 0.2));
}

.timeline-content {
  flex: 1;
  padding: 8px 0;
}

.timeline-step-name {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4px;
}

.timeline-date {
  font-size: 13px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
}

/* Stage Section */
.stage-section {
  margin-bottom: 24px;
  animation: fade-in 0.4s ease;
}

.quick-select {
  margin-bottom: 20px;
}

.quick-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
}

.stage-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-chip {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Sora', sans-serif;
}

.stage-chip:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.stage-chip.active {
  border-color: #00fff5;
  background: rgba(0, 255, 245, 0.15);
  color: #00fff5;
  box-shadow: 0 0 15px rgba(0, 255, 245, 0.3);
}

.chip-not-started { color: #9ca3af; }
.chip-designing { color: #818cf8; }
.chip-developing { color: #a78bfa; }
.chip-testing { color: #fbbf24; }
.chip-completed { color: #4ade80; }

.stage-select-wrapper {
  margin-bottom: 24px;
}

.stage-select-wrapper label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Space Mono', monospace;
  margin-bottom: 8px;
}

.stage-select {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.stage-select:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.stage-select option {
  background: #1a0b2e;
  color: #ffffff;
}

/* Update Button */
.btn-update {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 255, 245, 0.4);
  font-family: 'Sora', sans-serif;
}

.btn-update:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(0, 255, 245, 0.6);
}

.btn-update:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Message Alert */
.message-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 24px;
  animation: fade-in 0.3s ease;
}

.message-alert.success {
  background: rgba(74, 222, 128, 0.15);
  border: 2px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
}

.message-alert.error {
  background: rgba(239, 68, 68, 0.15);
  border: 2px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.message-alert button {
  margin-left: auto;
  background: transparent;
  border: none;
  color: currentColor;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.message-alert button:hover {
  opacity: 1;
}

/* Snackbar */
.snackbar {
  position: fixed;
  bottom: 32px;
  right: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid #00fff5;
  border-radius: 12px;
  color: #ffffff;
  font-size: 14px;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 0 40px rgba(0, 255, 245, 0.5),
    0 8px 30px rgba(0, 0, 0, 0.5);
  z-index: 2000;
  animation: snackbar-in 0.3s ease;
}

@keyframes snackbar-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.snackbar button {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.snackbar button:hover {
  opacity: 1;
}

/* Transitions */
.slide-down-enter-active {
  animation: slide-down 0.3s ease;
}

.slide-down-leave-active {
  animation: slide-down 0.2s ease reverse;
}

@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .progress-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-card {
    width: 100%;
  }

  .nav-container {
    flex-direction: column;
    gap: 12px;
  }

  .nav-brand {
    font-size: 12px;
  }

  .customer-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .summary-date {
    width: 100%;
  }

  .stage-chips {
    flex-direction: column;
  }

  .stage-chip {
    width: 100%;
  }
}
</style>